/**
 * Pure status helpers for the notification-engine shadow-comparison
 * visualization (TMS-1660). Split out from the rendering components so the
 * status-to-color mapping and the summary-panel ordering/zero-fill logic are
 * unit-testable without rendering the page.
 */
import { exhaustive } from "shared/switch";
import { NotificationShadowStatus } from "../../generated/graphql";

/**
 * Canonical display order for the three persisted resolution statuses. A
 * MATCH is never included: it self-prunes at write time (row deleted, only a
 * metric kept), so it can never appear in this table or its summary.
 */
export const SHADOW_STATUS_ORDER: readonly NotificationShadowStatus[] = [
  NotificationShadowStatus.Pending,
  NotificationShadowStatus.Mismatch,
  NotificationShadowStatus.Orphan,
];

/** MUI Chip color for a shadow-comparison status. */
export const shadowStatusChipColor = (
  status: NotificationShadowStatus,
): "default" | "error" | "warning" => {
  switch (status) {
    case NotificationShadowStatus.Mismatch: {
      return "error";
    }
    case NotificationShadowStatus.Orphan: {
      return "warning";
    }
    case NotificationShadowStatus.Pending: {
      return "default";
    }
    default: {
      return exhaustive(status);
    }
  }
};

/** One resolution status paired with its row count. */
export type ShadowStatusCount = {
  status: NotificationShadowStatus;
  count: number;
};

/**
 * Reorders summary rows into {@link SHADOW_STATUS_ORDER} and fills in a zero
 * count for any status the summary query omitted, so the at-a-glance panel
 * always renders all three statuses in a stable position regardless of what
 * the backend returned (including an empty "shadow is clean" result).
 */
export const orderedSummaryCounts = (
  entries: readonly ShadowStatusCount[],
): ShadowStatusCount[] => {
  const countByStatus = new Map(
    entries.map((entry) => [entry.status, entry.count]),
  );
  return SHADOW_STATUS_ORDER.map((status) => ({
    status,
    count: countByStatus.get(status) ?? 0,
  }));
};
